import React, { useCallback, useEffect, useState } from "react";
import sharedStyles from "../Shared.module.scss";
import styles from "./CampaignOrders.module.scss";
import { strings } from "../../../../../../intl/fa";
import CampaignEmptyState from "./CampaignEmptyState/CampaignEmptyState";
import {
  Button,
  ButtonSize,
  CloseIcon,
  Download,
  NewButton,
  NewButtonSize,
  NewButtonVariant,
  PlusSquare,
} from "@bale/ui";
import { useAppDispatch, useAppSelector } from "../../../../../../utils/store";
import { useRequest } from "../../../../../../hooks/useRequest";
import { getCampaignAdsThunk } from "../../../../../../core/ordersList/ordersList.thunk";
import {
  AppPage,
  NavigationActions,
  AppPageUrls,
} from "../../../../../../core/navigation/navigation.slice";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AdsListShimmer,
  CampaignStateText,
  CampaignTypeText,
  OrderItemsList,
} from "../Shared";
import { useOrdersListState } from "../orderTabs.utils";
import CampaignItem, {
  useOpenCampaignDetails,
} from "./campaignItem/CampaignItem";
import classNames from "classnames";
import {
  OrderAdActions,
  OrderAdPages,
} from "../../../../../../core/orderAd/orderAd.slice";
import { useMyPanel } from "../../../../../../hooks/useMyPanel";
import { toast } from "react-toastify";
import { getCreditThunk } from "../../../../../../core/auth/auth.thunk";
import { sendYandexGoal, YandexGoal } from "../../../../../../yandex/yandex";
import { useTheme } from "@emotion/react";
import { digitsToHindi, percentCalc } from "@bale/utils";
import { exportToExcel } from "utils/utils";
import Footer from "components/commons/footer/Footer";
import { isISOTimeAfterToday } from "utils/time";
import FullpageModal from "components/commons/modal/fullpageModal/FullpageModal";
import { PackageItem } from "components/content/packagesPage/PackageItem";
import ReactDOM from "react-dom";
import PackagesModal from "components/content/packagesPage/PackagesModal";
import { useNeedAuth } from "hooks/useNeedAuth";
import { AccountType, FeatureScope } from "core/auth/auth.entity";
import { useFeatureScopes } from "hooks/useFeatureScopes";
import { PackageInfo } from "components/content/packagesPage/packages";
import useIsLargeScreen from "../../hooks/useIsDesktop";

type Props = {
  disableButton: boolean;
};
export const CampaignOrders: React.FC<React.PropsWithChildren<Props>> = (
  props
) => {
  const { sendRequest: getCampaigns, loading } =
    useRequest(getCampaignAdsThunk);
  useRequest(getCreditThunk, true);
  const campaignsList = useAppSelector((state) => state.ordersList.campaigns);
  const dispatch = useAppDispatch();
  const { showDivider, showEmptyState, showShimmer, showList, showChildren } =
    useOrdersListState(loading, campaignsList, getCampaigns);
  const myPanel = useMyPanel();
  const campaignsData = useAppSelector((state) => state.ordersList.campaigns);
  const { customPackageInfo } = useAppSelector((state) => state.orderAd);
  const isDesktop = useIsLargeScreen(1400);

  const search = useLocation().search;
  const navigate = useNavigate();
  const selectCampaign = useOpenCampaignDetails();
  useEffect(() => {
    const params = new URLSearchParams(search);
    const id = params.get("id");
    if (id) {
      const order = campaignsList.find((order) => order.id === id);
      if (order) {
        navigate(AppPageUrls[AppPage.OrdersList], { replace: true });
        selectCampaign(order);
      }
    }
  }, [campaignsList]);

  const { needsAuth } = useNeedAuth();

  const invoicingCallback = () => {
    dispatch(NavigationActions.setAppPage(AppPage.SubmitAd));
  };

  const openSubmitAdPage = useCallback(() => {
    if (needsAuth) {
      dispatch(NavigationActions.setAppPage(AppPage.Invoicing));
      if (myPanel.type === AccountType.Personal) {
        dispatch(NavigationActions.setNavigationCallback(invoicingCallback));
      }
      return;
    }
    if (myPanel.credit <= 0) {
      toast.info(strings.ad_sale.increase_your_credit);
    } else if (!isISOTimeAfterToday(myPanel.credit_expire_time)) {
      toast.info(strings.ad_sale.credit_has_expired);
    } else {
      dispatch(NavigationActions.setAppPage(AppPage.SubmitAd));
      dispatch(OrderAdActions.setPage(OrderAdPages.CampaignSelectAdType));
      sendYandexGoal(YandexGoal.CreateCampaignOrderBtn);
    }
  }, [myPanel]);

  const exportHandler = () => {
    const exportingCampaignsData = campaignsData.map((campaignData) => {
      const { ads, state, id } = campaignData;
      const { title, description, link, type, view_count, click_count, cost } =
        ads[0];
      return {
        شناسه: id,
        وضعیت: CampaignStateText[state],
        "عنوان کمپین": title,
        "متن کمپین": description,
        "لینک کمپین": link,
        "نوع کمپین": CampaignTypeText[type],
        "بازدیدها تا این لحظه": view_count,
        "کلیک‌ها تا این لحظه": click_count,
        // "نرخ کلیک": percentCalc(click_count, view_count),
        "هزینه تا این لحظه": cost,
      };
    });
    exportToExcel(exportingCampaignsData, "campaign-report", "campaigns");
  };

  const theme = useTheme();

  return (
    <>
      {customPackageInfo?.name && (
        <CampaignNotif packageInfo={customPackageInfo} />
      )}
      {showShimmer && <AdsListShimmer />}
      {showChildren && props.children && (
        <div
          className={classNames(sharedStyles.ChildrenWrapper, {
            [sharedStyles.hasDivider]: showDivider,
          })}
        >
          {props.children}
        </div>
      )}
      {showEmptyState && (
        <CampaignEmptyState onButtonClick={openSubmitAdPage} />
      )}
      {showList && (
        <OrderItemsList>
          <div className={styles.ButtonsContainer}>
            <NewButton
              size={NewButtonSize.MEDIUM}
              icon={PlusSquare}
              className={sharedStyles.AddButton}
              onClick={openSubmitAdPage}
            >
              {strings.ad_sale.create_campaign}
            </NewButton>
            <div className={styles.DownloadCampaign} onClick={exportHandler}>
              <Download size={24} color={theme.colors.primary[50]} />
              <span>{strings.ad_sale.download_campaigns_report}</span>
            </div>
          </div>
          <div className={styles.list}>
            {isDesktop && (
              <div className={styles.tableHeader}>
                <p className={styles.col}>
                  {strings.ad_sale.ordered_ads_list.adImage}
                </p>
                <p className={styles.col}>
                  {strings.dialog_ad_form.info_step.title_label}
                </p>
                <p className={styles.col}>
                  {strings.dialog_ad_form.info_step.text_label}
                </p>
                <p className={styles.col}>
                  {strings.ad_sale.ordered_ads_list.adSpot}
                </p>
                <p className={styles.col}>
                  {strings.ad_sale.ordered_ads_list.adStatus}
                </p>
                <p className={styles.col}>{strings.stop_campaign.title}</p>
                <p className={`${styles.col} ${styles.startCampaign}`}>
                  {strings.ad_sale.ordered_ads_list.startCampaign}
                </p>
                <p className={styles.col}>
                  {strings.ad_sale.ordered_ads_list.more}
                </p>
              </div>
            )}
            {campaignsList.map((order) => (
              <CampaignItem campaign={order} key={order.id} />
            ))}
          </div>
        </OrderItemsList>
      )}
      <Footer mobileOnly>
        <NewButton
          size={NewButtonSize.MEDIUM}
          style={{ width: "100%", margin: "0 16px" }}
          icon={PlusSquare}
          text={strings.ad_sale.create_campaign}
          onClick={openSubmitAdPage}
          disabled={props.disableButton}
        />
      </Footer>
    </>
  );
};

function CampaignNotif(props: { packageInfo: PackageInfo }) {
  const [showCustomPack, setShowCustomPack] = useState(false);
  const [showAllPackages, setShowAllPackages] = useState(false);
  const showCustomPackDetail = useCallback(() => {
    setShowCustomPack(true);
  }, []);

  const isFestival = useFeatureScopes(FeatureScope.Festival);

  const showOtherPacks = useCallback(() => {
    setShowAllPackages(true);
  }, []);

  const theme = useTheme();
  return (
    <>
      <div className={styles.CampaignNotif}>
        <p
          style={{
            color: isFestival
              ? theme.colors.alternative[3]
              : theme.colors.dark[500],
          }}
          className={styles.NotifTitle}
        >
          {isFestival
            ? strings.side_bar.yalda_package.title
            : strings.side_bar.custom_pack_notif.your_custom_pack}
        </p>
        <p className={styles.NotifDesc}>
          {isFestival
            ? strings.side_bar.yalda_package.description
            : strings.side_bar.custom_pack_notif.pack_created_by_admin}
        </p>
        <div className={styles.ButtonContainer}>
          <NewButton
            className={styles.NotifButton}
            size={NewButtonSize.SMALL}
            onClick={showCustomPackDetail}
            variant={NewButtonVariant.OUTLINED}
          >
            {strings.common.button.more_info}
          </NewButton>
          <p className={styles.OtherPacks} onClick={showOtherPacks}>
            {strings.side_bar.custom_pack_notif.other_packs}
          </p>
        </div>
      </div>
      {showCustomPack &&
        ReactDOM.createPortal(
          <FullpageModal>
            <div className={styles.CloseIcon}>
              <CloseIcon size={32} onClick={() => setShowCustomPack(false)} />
            </div>
            <p className={styles.FullModalTitle}>
              {isFestival
                ? strings.packages.yalda.title
                : strings.packages.sidebar.your_package}
            </p>
            {isFestival ? (
              <>
                <p className={styles.FullModalDesc}>
                  <span className={styles.FullModalDesc}>
                    {strings.packages.yalda.description1}
                  </span>
                  <span
                    style={{ color: theme.colors.alternative[3] }}
                    className={styles.FullModalDesc}
                  >
                    {digitsToHindi(strings.packages.yalda.description2)}
                  </span>
                  <span className={styles.FullModalDesc}>
                    {" "}
                    {strings.packages.yalda.description3}
                  </span>
                </p>
              </>
            ) : (
              <p className={styles.FullModalDesc}>
                {strings.packages.modal.buy_new_pack_desc}
              </p>
            )}
            <PackageItem isFestival={isFestival} package={props.packageInfo} />
          </FullpageModal>,
          document.getElementById("fullscreen-modal")!
        )}
      {showAllPackages &&
        ReactDOM.createPortal(
          <PackagesModal onClose={() => setShowAllPackages(false)} />,
          document.getElementById("fullscreen-modal")!
        )}
    </>
  );
}
export default CampaignOrders;
